import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const DISCORD_INVITE = 'https://discord.gg/3MBKc7yt9u';

const DISCORD_SVG = (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
        <path d="M20.317 4.37a19.791 19.791 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.037A19.736 19.736 0 003.677 4.37a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 00.031.057 19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128 10.2 10.2 0 00.372-.292.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.01c.12.098.246.198.373.292a.077.077 0 01-.006.127 12.299 12.299 0 01-1.873.892.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.839 19.839 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
    </svg>
);

function Discord() {
    const { user } = useAuth();

    if (user?.discordLinked) {
        return (
            <div className="flex-1 flex items-center justify-center p-3 md:p-4 overflow-auto">
                <div className="max-w-2xl w-full">
                    {/* Command bar header */}
                    <div className="flex items-center justify-between px-4 py-3 bg-slate-800/40 border border-slate-700/50 rounded-t">
                        <div className="flex items-center gap-3">
                            <h2 className="text-sm font-semibold text-white tracking-tight">Discord</h2>
                            <div className="w-px h-4 bg-slate-700/50" />
                            <div className="flex items-center gap-1.5">
                                <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
                                <span className="text-[10px] font-mono font-semibold text-emerald-400 uppercase tracking-widest">Connected</span>
                            </div>
                        </div>
                        <span className="text-[11px] font-mono text-slate-600 hidden sm:block">{user?.email}</span>
                    </div>

                    {/* Status panel */}
                    <div className="border border-t-0 border-slate-700/50 rounded-b bg-slate-900/30">
                        {/* Description */}
                        <div className="px-4 py-4 border-b border-slate-700/30">
                            <p className="text-xs font-mono text-slate-500 leading-relaxed">
                                Your Discord account is linked to your Snipe account. You have access to exclusive channels, bot features, and the community based on your subscription tier.
                            </p>
                        </div>

                        {/* Status rows */}
                        <div className="divide-y divide-slate-700/20">
                            <div className="flex items-center justify-between px-4 py-3">
                                <span className="text-[10px] font-mono text-slate-600 uppercase tracking-widest">Connection</span>
                                <div className="flex items-center gap-1.5">
                                    <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />
                                    <span className="text-xs font-mono font-medium text-emerald-400">Active</span>
                                </div>
                            </div>
                            <div className="flex items-center justify-between px-4 py-3">
                                <span className="text-[10px] font-mono text-slate-600 uppercase tracking-widest">Channels</span>
                                <span className="text-xs font-mono font-medium text-slate-300">Tier-based access</span>
                            </div>
                            <div className="flex items-center justify-between px-4 py-3">
                                <span className="text-[10px] font-mono text-slate-600 uppercase tracking-widest">Community</span>
                                <span className="text-xs font-mono font-medium text-slate-300">Enabled</span>
                            </div>
                            <div className="flex items-center justify-between px-4 py-3">
                                <span className="text-[10px] font-mono text-slate-600 uppercase tracking-widest">Alerts</span>
                                <span className="text-xs font-mono font-medium text-slate-300">Real-time via bot</span>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="px-4 py-4 border-t border-slate-700/30 flex flex-col sm:flex-row gap-2">
                            <a
                                href={DISCORD_INVITE}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-500/10 border border-emerald-500/20 rounded text-xs font-mono font-medium text-emerald-400 hover:bg-emerald-500/15 transition-colors"
                            >
                                {DISCORD_SVG}
                                Open Discord Server
                            </a>
                            <Link
                                to="/manage-subscriptions"
                                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded text-xs font-mono font-medium text-slate-300 hover:text-white hover:border-slate-600 transition-colors"
                            >
                                Manage Subscriptions
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                </svg>
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const steps = [
        {
            number: 1,
            title: 'Join Discord',
            description: 'Click the button below to join the Snipe Discord community — access EV Bots and connect with members.',
            isDiscordButton: true,
        },
        {
            number: 2,
            title: 'Authenticate Account',
            customDescription: (
                <>
                    Navigate to the <span className="text-emerald-400 font-semibold">#authenticate</span> channel and type:
                </>
            ),
            command: '/authenticate',
        },
        {
            number: 3,
            title: 'Confirm Email',
            customDescription: (
                <>
                    Enter your email address. <span className="text-emerald-400 font-semibold">Use the same email you signed up with</span> for verification.
                </>
            ),
        },
        {
            number: 4,
            title: 'Access Granted',
            description: 'You will be assigned roles to access all EV Bot channels based on your subscription tier.',
        },
        {
            number: 5,
            title: 'Manage Subscription',
            description: 'View and manage your subscription, upgrade or downgrade your plan, and access billing history.',
            link: '/manage-subscriptions',
            linkText: 'Manage Subscriptions',
        },
    ];

    return (
        <div className="flex-1 flex items-center justify-center p-3 md:p-4 overflow-auto">
            <div className="max-w-xl w-full">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 bg-slate-800/40 border border-slate-700/50 rounded-t">
                    <div className="flex items-center gap-3">
                        <h2 className="text-sm font-semibold text-white tracking-tight">Discord Setup</h2>
                        <div className="w-px h-4 bg-slate-700/50" />
                        <div className="flex items-center gap-1.5">
                            <div className="w-1.5 h-1.5 bg-amber-400 rounded-full" />
                            <span className="text-[10px] font-mono font-semibold text-amber-400 uppercase tracking-widest">Pending</span>
                        </div>
                    </div>
                    <span className="text-[10px] font-mono text-slate-600">{steps.length} steps</span>
                </div>

                {/* Timeline */}
                <div className="border border-t-0 border-slate-700/50 rounded-b bg-slate-900/30">
                    <div className="px-4 py-4">
                        <p className="text-xs font-mono text-slate-500 leading-relaxed">
                            Follow these steps to link your Discord account and get started.
                        </p>
                    </div>

                    <div className="px-4 pb-4">
                        {steps.map((step, index) => (
                            <div key={step.number} className="relative flex gap-4">
                                {/* Timeline line + dot */}
                                <div className="flex flex-col items-center">
                                    <div className="w-6 h-6 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
                                        <span className="text-[9px] font-mono font-bold text-emerald-400">{step.number}</span>
                                    </div>
                                    {index < steps.length - 1 && (
                                        <div className="w-px flex-1 bg-slate-700/40 my-1" />
                                    )}
                                </div>

                                {/* Step content */}
                                <div className={`flex-1 ${index < steps.length - 1 ? 'pb-5' : 'pb-1'}`}>
                                    <h3 className="text-sm font-medium text-white mb-1">{step.title}</h3>
                                    <p className="text-xs font-mono text-slate-500 leading-relaxed">
                                        {step.customDescription || step.description}
                                    </p>

                                    {step.command && (
                                        <div className="mt-2 inline-flex items-center gap-2 bg-slate-800 rounded px-3 py-2 border border-slate-700/50">
                                            <span className="text-[10px] font-mono text-slate-600">$</span>
                                            <code className="text-xs font-mono font-medium text-emerald-400">{step.command}</code>
                                        </div>
                                    )}

                                    {step.isDiscordButton && (
                                        <a
                                            href={DISCORD_INVITE}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="mt-2 inline-flex items-center gap-2 px-3.5 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded text-xs font-mono font-medium text-emerald-400 hover:bg-emerald-500/15 transition-colors"
                                        >
                                            {DISCORD_SVG}
                                            Join Discord Server
                                        </a>
                                    )}

                                    {step.link && (
                                        <Link
                                            to={step.link}
                                            className="mt-2 inline-flex items-center gap-1 text-xs font-mono font-medium text-emerald-400 hover:text-emerald-300 transition-colors"
                                        >
                                            {step.linkText}
                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                            </svg>
                                        </Link>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Help footer */}
                    <div className="px-4 py-3 border-t border-slate-700/30">
                        <p className="text-[11px] font-mono text-slate-600">
                            Need help? Email{' '}
                            <a href="mailto:info@snipeaio.com" className="text-emerald-400/80 hover:text-emerald-400 transition-colors">
                                info@snipeaio.com
                            </a>
                            {' '}or reach out in Discord.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Discord;
